import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef } from 'react';

import { styled } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { modalClasses } from '@o2/ui/Modal';
import { BUTTON_BORDER_RADIUS } from '@o2/ui/theme/constants';

export type MenuItemProps<T extends ElementType = 'button'> = {
  /**
   * This prop currently does nothing visually, only sets `aria-current`.
   */
  active?: boolean;

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;

  ref?: ComponentPropsWithRef<T>['ref'];
  /**
   * Internal prop changes color of the item.
   */
  internal?: boolean;
} & ComponentPropsWithoutRef<T>;

const MenuItemRoot = styled('button', {
  name: 'AppBar',
  slot: 'MenuItem',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'internal',
})<MenuItemProps>(({ theme, internal }) => ({
  fontFamily: theme.typography.fontFamily,
  display: 'flex',
  flexDirection: 'row',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: 4,
  fontSize: 16,
  fontWeight: 500,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: theme.spacing(0, 2),
  height: '100%',
  color: theme.palette.primary.main,
  transition: theme.transitions.create(['background-color'], { duration: theme.transitions.duration.standard }),
  '&:hover': {
    backgroundColor: colors.transparentWhite[100],
    textDecoration: 'underline',
    textUnderlineOffset: '0.3em',
    border: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1.5),
  },
  ...(internal && {
    height: `calc(100% - ${theme.spacing(2)})`,
    margin: theme.spacing(1),
    backgroundColor: theme.palette.internal.main,
    color: theme.palette.common.white,
    borderRadius: BUTTON_BORDER_RADIUS,
    '&:hover': {
      backgroundColor: theme.palette.internal.dark,
      border: 'none',
    },
    // Different styles for internal items in dropdown menu
    [`.${modalClasses.root} &`]: {
      // Minimal height to 40px (same as for other items in dropdown menu).
      minHeight: '40px',
      margin: 0,
      backgroundColor: colors.internal['100'],
      color: theme.palette.primary.main,
      borderRadius: 0,
      '&:hover': {
        backgroundColor: colors.brandPrimary['100'],
        textDecoration: 'underline',
        textUnderlineOffset: '0.3em',
      },
    },
  }),
}));

/**
 * MenuItem is a styled button. Must be inside `AppBar.Menu`.
 */
const MenuItem = forwardRef(
  <T extends ElementType = 'button'>({ active, ...props }: MenuItemProps<T>, ref: MenuItemProps['ref']) => {
    return <MenuItemRoot aria-current={active ? 'page' : undefined} {...props} ref={ref} />;
  }
);

MenuItem.displayName = 'AppBar.MenuItem';

export default MenuItem;
